/**
 * Engineering-Stückliste (VB-26025): Baum, Positionsnummern, Gesamtmengen,
 * Kumulierung je Artikel. Bewusst frei von I/O — das ist die Rechenlogik, die
 * heute in der Excel-Matrix steckt, und sie muss ohne DB prüfbar sein (§10).
 *
 * Gespeichert wird die Struktur über `parentItemId` (Selbst-Referenz), NICHT über
 * die Positionsnummer: `1.1.2` ist eine Anzeige der Struktur. Als Wahrheit
 * gespeichert bräuchte jedes Einfügen eine Neunummerierung aller Nachbarn — und
 * ein Tippfehler zerlegt den Baum.
 */

/** Zeilentyp — entspricht Spalte A des SolidWorks-Exports (leer / `Titel` / `Z`). */
export const BOM_ITEM_TYPES = ['POSITION', 'SECTION', 'NOTE'] as const;
export type BomItemType = (typeof BOM_ITEM_TYPES)[number];

/**
 * Tiefe des Baums: oberste Ebene + 4 Unterebenen. Grenze aus der Praxis
 * (Angabe zum SolidWorks-Export in VB-26025), nicht aus der Technik — sie
 * verhindert vor allem, dass ein Import-Fehler einen endlos tiefen Baum baut.
 */
export const MAX_BOM_DEPTH = 5;

/** Die Felder, die der Baum braucht. Absichtlich schmal, damit DB-Zeile und Entwurf beide passen. */
export interface BomItemLike {
  id: string;
  parentItemId: string | null;
  /** Reihenfolge unter demselben Elternteil. */
  position: number;
  type: BomItemType;
  /** Menge je Elternteil-Einheit; null bei Abschnitt und Zusatztext. */
  quantity: number | null;
  /**
   * Stückzahl, wenn die Menge eine Länge ist (Meterware, ADR 0040): „12 Stück á
   * 5920 mm" steht als `quantity` 71,04 m + `pieceCount` 12. Optional, weil der
   * Baum auch mit Zeilen rechnen muss, die keine kennen (Auslegung, Vorlage).
   */
  pieceCount?: number | null;
  articleId: string | null;
  articleNumber: string | null;
  description: string;
  unit: string | null;
}

/** Eine Zeile im Baum, um die gerechneten Werte ergänzt. */
export interface BomNode<T extends BomItemLike = BomItemLike> {
  item: T;
  /** `1.1.2` — aus der Baumposition abgeleitet; `''` bei Abschnitt und Zusatztext. */
  posNumber: string;
  /** 0 = oberste Ebene. */
  depth: number;
  /**
   * Menge × Mengen ALLER Elternteile. `null` bei Abschnitt und Zusatztext —
   * die haben keine Menge, und 0 würde „null Stück" behaupten.
   */
  totalQuantity: number | null;
  /** Die Mengen der Elternteile, von oben nach unten — für die Anzeige der Rechnung. */
  factors: number[];
  children: BomNode<T>[];
}

/**
 * Nur Positionen tragen eine Menge, die sich nach unten durchmultipliziert.
 * Ein Abschnitt („Titel") ist eine Überschrift und muss durchsichtig sein: er
 * darf die Mengen seiner Kinder nicht verändern.
 *
 * **Meterware zählt in Stück** (ADR 0095, dieselbe Regel wie in ADR 0093 für die
 * Ableitung): Unter einem Balken mit 71,04 m / 12 Stück hängen die Schrauben für
 * ZWÖLF Balken, nicht für 71 Meter. Wo eine Stückzahl steht, ist sie die
 * treibende Zahl — sonst müsste die Menge des Kindes ein Bruch je Meter sein
 * (1,6892 Scheiben je Meter rechnet richtig und liest sich wie ein Fehler).
 */
function multiplierOf(item: BomItemLike): number {
  if (item.type !== 'POSITION') return 1;
  return item.pieceCount ?? item.quantity ?? 1;
}

/**
 * Flache Liste → Baum.
 *
 * Zeilen, deren Elternteil nicht in der Liste steht, hängen sich oben ein statt
 * zu verschwinden — eine Stückliste, die beim Anzeigen still Positionen
 * unterschlägt, wäre schlimmer als eine mit einer falsch eingerückten Zeile.
 * Zeilen in einem Zyklus (A → B → A) sind von keiner Wurzel aus erreichbar und
 * tauchen deshalb nicht auf; `findBomCycleIds` benennt sie.
 */
export function buildBomTree<T extends BomItemLike>(items: readonly T[]): BomNode<T>[] {
  const byId = new Map<string, T>(items.map((item) => [item.id, item]));
  const cycles = findBomCycleIds(items);
  const childrenOf = new Map<string | null, T[]>();
  for (const item of items) {
    if (cycles.has(item.id)) continue;
    // Elternteil unbekannt oder selbst im Zyklus ⇒ wie eine Wurzel behandeln.
    // Sonst hinge diese Zeile an etwas, das nie gezeichnet wird, und wäre weg.
    const parentId = item.parentItemId;
    const key = parentId && byId.has(parentId) && !cycles.has(parentId) ? parentId : null;
    const bucket = childrenOf.get(key);
    if (bucket) bucket.push(item);
    else childrenOf.set(key, [item]);
  }

  const build = (parentId: string | null, prefix: string, depth: number, factors: number[]): BomNode<T>[] => {
    const children = [...(childrenOf.get(parentId) ?? [])].sort(compareBomItems);
    let counter = 0;
    return children.map((item) => {
      // Nur Positionen bekommen eine Nummer — im Excel steht neben einem
      // Zusatztext ebenfalls keine POS-NR.
      const posNumber = item.type === 'POSITION' ? joinPos(prefix, (counter += 1)) : '';
      const factor = factors.reduce((product, value) => product * value, 1);
      const quantity = item.type === 'POSITION' ? item.quantity ?? 0 : null;
      const nextFactors = [...factors, multiplierOf(item)];
      return {
        item,
        posNumber,
        depth,
        totalQuantity: quantity == null ? null : quantity * factor,
        factors: [...factors],
        // Der Präfix der Kinder ist die Nummer dieser Zeile; hat sie keine
        // (Abschnitt), zählen die Kinder auf der Ebene darüber weiter.
        children: build(item.id, posNumber || prefix, depth + 1, nextFactors),
      };
    });
  };

  return build(null, '', 0, []);
}

/** `1.1` + `2` → `1.1.2`. */
function joinPos(prefix: string, index: number): string {
  return prefix ? `${prefix}.${index}` : String(index);
}

/** Stabile Reihenfolge unter einem Elternteil: Position, dann Id als Gleichstands-Regel. */
export function compareBomItems(a: BomItemLike, b: BomItemLike): number {
  if (a.position !== b.position) return a.position - b.position;
  return a.id.localeCompare(b.id);
}

/**
 * Ids aller Zeilen, die über `parentItemId` in einem Zyklus hängen. Die DB kann
 * das nicht verhindern (Selbst-Referenz ohne Tiefenprüfung), und ein Zyklus
 * würde jeden rekursiven Aufbau in eine Endlosschleife schicken.
 */
export function findBomCycleIds(items: readonly BomItemLike[]): Set<string> {
  const parentOf = new Map<string, string | null>(items.map((item) => [item.id, item.parentItemId]));
  const safe = new Set<string>();
  const cyclic = new Set<string>();

  for (const item of items) {
    const path: string[] = [];
    const seen = new Set<string>();
    let current: string | null = item.id;
    while (current && parentOf.has(current) && !safe.has(current)) {
      if (seen.has(current)) {
        // Ab dem Wiedereintritt hängt alles auf dem Pfad im Zyklus.
        for (const id of path.slice(path.indexOf(current))) cyclic.add(id);
        break;
      }
      seen.add(current);
      path.push(current);
      current = parentOf.get(current) ?? null;
    }
    for (const id of path) if (!cyclic.has(id)) safe.add(id);
  }
  return cyclic;
}

/** Baum → flache Liste in Anzeige-Reihenfolge (Tiefe zuerst). */
export function flattenBomTree<T extends BomItemLike>(nodes: readonly BomNode<T>[]): BomNode<T>[] {
  const out: BomNode<T>[] = [];
  const walk = (list: readonly BomNode<T>[]): void => {
    for (const node of list) {
      out.push(node);
      walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

/** Tiefe des Baums (0 = leer, 1 = nur oberste Ebene). */
export function bomTreeDepth<T extends BomItemLike>(nodes: readonly BomNode<T>[]): number {
  return nodes.reduce((max, node) => Math.max(max, 1 + bomTreeDepth(node.children)), 0);
}

/** Eine Zeile der kumulierten Bedarfsliste. */
export interface BomCumulatedEntry {
  /** Soft-Ref auf den Artikelstamm; null bei Freitext-Zeilen. */
  articleId: string | null;
  articleNumber: string | null;
  description: string;
  unit: string | null;
  quantity: number;
  /** Positionsnummern, aus denen die Menge stammt — die Herkunft muss sichtbar bleiben. */
  positions: string[];
}

/**
 * Identität einer Zeile für die Kumulierung: der Artikel (ersatzweise die
 * Nummer, ersatzweise der Freitext) UND die Einheit.
 *
 * Als eigene Funktion, weil außer `cumulateByArticle` auch der Übertrag in den
 * Materialbedarf (`bom-material.ts`) einer kumulierten Zeile ihre Herkunft im
 * Baum zuordnen muss. Zwei Fassungen dieses Schlüssels wären zwei Wahrheiten:
 * eine Zeile fiele in der einen Rechnung zusammen und in der anderen nicht.
 */
export function bomCumulateKey(
  item: Pick<BomItemLike, 'articleId' | 'articleNumber' | 'description' | 'unit'>,
): string {
  const identity = item.articleId ?? item.articleNumber ?? `frei:${item.description}`;
  // Trennzeichen \0, weil eine Bezeichnung Leerzeichen enthalten darf: mit einem
  // Leerzeichen als Trenner könnten zwei verschiedene Zeilen denselben Schlüssel
  // bilden und würden zusammengezählt.
  return `${identity} ${item.unit ?? ''}`;
}

/**
 * Summe je Artikel über den ganzen Baum — das, was heute die Pivot-Tabelle
 * „Bef. kommuliert" leistet, und die Grundlage der Bedarfsliste (ADR 0080).
 *
 * Gezählt werden **nur Blätter**: Wer eine Baugruppe UND ihre Einzelteile
 * bestellt, bestellt doppelt. Eine Baugruppe ohne eigene Zeilen ist selbst ein
 * Blatt und wird darum mitgezählt — genau richtig für Zukaufteile.
 *
 * Verschiedene Einheiten werden **nie** zusammengezählt; sie ergeben getrennte
 * Zeilen. Zwei Meter Kette und zwei Stück Kette sind nicht vier von irgendwas.
 */
export function cumulateByArticle<T extends BomItemLike>(
  nodes: readonly BomNode<T>[],
): BomCumulatedEntry[] {
  const entries = new Map<string, BomCumulatedEntry>();
  for (const node of flattenBomTree(nodes)) {
    if (node.item.type !== 'POSITION') continue;
    if (node.children.length > 0) continue;
    if (node.totalQuantity == null) continue;

    const key = bomCumulateKey(node.item);
    const existing = entries.get(key);
    if (existing) {
      existing.quantity += node.totalQuantity;
      existing.positions.push(node.posNumber);
      continue;
    }
    entries.set(key, {
      articleId: node.item.articleId,
      articleNumber: node.item.articleNumber,
      description: node.item.description,
      unit: node.item.unit,
      quantity: node.totalQuantity,
      positions: [node.posNumber],
    });
  }
  return [...entries.values()];
}
